import { useCallback, useEffect, useMemo, useState } from 'react';
import { Home as HomeIcon } from '@mui/icons-material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import slugify from 'slugify';
import { PageActionsProps } from 'components/ActionButtons/types';
import {
  dashboardCompanyInformationUrl,
  dashboardCompanyOperationsUrl,
  dashboardCompanyOverviewUrl,
  dashboardCompanySettingsUrl,
  dashboardCompanySubscriptionUrl,
  WORKBOARD_CURRENT_JOBS_URL,
  WORKBOARD_MODELS_URL,
  WORKBOARD_OPPORTUNITIES_URL,
  WORKBOARD_PAST_JOBS_URL,
} from 'dashboard409a/common/config/urls';
import {
  COMPANY_DETAILS_SLUGS,
  LOG_OUT,
  SETTINGS_SLUG,
  SUBSCRIPTION_SLUG,
  VIEW_DETAILS,
} from 'dashboard409a/common/constants';
import { IBottomMenuItems, INavigationItem, ITopMenuItems, Sidebar } from 'dashboard409a/components';
import { AttioValuationProvider } from 'dashboard409a/context/AttioValuationContext';
import { CompanyDashboardContext } from 'dashboard409a/context/CompanyDashboardContext';
import { RootStateType } from 'dashboard409a/states/store';
import { useFindSlug } from 'dashboard409a/utilities';
import CompanyFilter from 'layouts/Main/components/Sidebar/components/FirmFilter';
import { useLogOutUser } from 'services/hooks/auth';
import { MainContainer } from './components';
import { IDashboardLayout } from './types';
import { ISubMenu } from '../../components/Sidebar/components/TopMenu/types';

const DashboardLayout = ({ children }: IDashboardLayout) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [pageActions, setPageActions] = useState<PageActionsProps | null |undefined>();
  const [editMode, setEditMode] = useState(false);
  const [filterSearch, setFilterSearch] = useState('');

  const navigate = useNavigate();
  const currentSlug = useFindSlug();

  const logOutUser = useLogOutUser();

  const user = useSelector((state: RootStateType) => state.user);

  const userIsStaff = useMemo(() => {
    if (user) {
      return user.is_staff;
    }
    return false;
  }, [user]);

  const userIsSuperuser = useMemo(() => Boolean(user?.is_superuser), [user]);

  const userHasCompanies = useMemo(() => {
    if (user) {
      return (user.standalone_companies_permissions?.length ?? 0) > 0;
    }
    return false;
  }, [user]);
  
  const { currentCompany, showLoadingProgressBar = false } = useSelector((state: RootStateType) => state.global);

  useEffect(() => {
    if (!isSidebarOpen) {
      setFilterSearch('');
    }
  }, [isSidebarOpen]);

  const bottomItems = useMemo(() => {
    const currentBottomItems = [] as IBottomMenuItems[];

    if (
      user?.standalone_companies_permissions
      && user?.standalone_companies_permissions?.length > 0
      && currentCompany
    ) {
      let filteredCompanies = user?.standalone_companies_permissions
        ?.filter(standaloneCompany => standaloneCompany.id !== (currentCompany.id ?? 0));

      if (filterSearch) {
        filteredCompanies = filteredCompanies.filter(standaloneCompany => {
          const companyName = standaloneCompany.name.toLowerCase();
          return companyName.includes(filterSearch.toLowerCase());
        });
      }

      if (filterSearch && !filteredCompanies.length) {
        filteredCompanies = [{
          id: 0,
          name: 'Not found',
          slug: 'not-found',
          icon_url: '',
          is_active: false,
          logo_url: '',
        }];
      }

      const subItems = filteredCompanies?.map(standaloneCompany => {
        if (standaloneCompany.name === 'Not found') {
          return {
            handleItemClick: () => {},
            slug: standaloneCompany.slug,
            title: standaloneCompany.name,
            disabled: true,
          };
        }

        return {
          handleItemClick: () => {
            navigate({
              pathname: dashboardCompanyOverviewUrl(standaloneCompany.slug),
              search: `?${new URLSearchParams({})}`,
            });
          },
          slug: standaloneCompany.slug,
          title: standaloneCompany.name,
        };
      });

      currentBottomItems.push({
        imageUrl: currentCompany?.icon_url ?? '',
        isLink: true,
        primaryText: currentCompany.name ?? '',
        secondaryText: VIEW_DETAILS,
        slug: dashboardCompanySettingsUrl(currentCompany.slug) ?? '',
        subItems,
        subItemsLabel: <CompanyFilter setFilterSearch={setFilterSearch} isCompanyFilter />,
      });
    }
    if (user.first_name && user.last_name) {
      currentBottomItems.push({
        displayAvatar: true,
        handleButtonClick: () => logOutUser(),
        imageUrl: user.profile?.picture_url ?? '',
        primaryText: `${user.first_name} ${user.last_name}`.trim(),
        secondaryText: LOG_OUT,
        slug: slugify(`${user.first_name} ${user.last_name}`.trim()),
      });
    }

    return currentBottomItems;
  }, [currentCompany, logOutUser, user, filterSearch, navigate]);

  const reportsSubMenuItems: ISubMenu[] = useMemo(() => {
    const subMenuItems: ISubMenu[] = [
      { slug: 'reports', title: 'Reports', url: dashboardCompanyOverviewUrl(currentCompany?.slug ?? '') },
    ];
    if (userIsStaff) {
      subMenuItems.push({
        slug: 'operations',
        title: 'Operations',
        url: dashboardCompanyOperationsUrl(currentCompany?.slug ?? ''),
      });
    }
    if (userIsStaff) {
      subMenuItems.push({
        slug: 'information',
        title: 'Information',
        url: dashboardCompanyInformationUrl(currentCompany?.slug ?? ''),
      });
    }
    if (userIsSuperuser) {
      subMenuItems.push({
        slug: 'settings',
        title: 'Settings',
        url: dashboardCompanySettingsUrl(currentCompany?.slug ?? ''),
      });
    }
    return subMenuItems;
  }, [currentCompany, userIsStaff, userIsSuperuser]);

  const workboardSubMenuItems: ISubMenu[] = useMemo(() => [
    { slug: 'current-jobs', title: 'Current Jobs', url: WORKBOARD_CURRENT_JOBS_URL },
    { slug: 'past-jobs', title: 'Past Jobs', url: WORKBOARD_PAST_JOBS_URL },
    { slug: 'opportunities', title: 'Opportunities', url: WORKBOARD_OPPORTUNITIES_URL },
    { slug: 'models', title: 'Models', url: WORKBOARD_MODELS_URL },
  ], []);

  const topMenuItems = useMemo(() => [
    ...(userIsStaff ? [{
      slug: 'workboard',
      title: 'Workboard',
      isParent: true,
      icon: <HomeIcon />,
      handleClick: () => {
        // Check if there's a saved workboard route in localStorage
        const savedRoute = getPreviousWorkboardRoute();
        if (savedRoute && savedRoute.includes('/workboard/')) {
          // Navigate to the saved route and clear it
          navigate(savedRoute);
          clearPreviousWorkboardRoute();
        } else {
          // Default to current jobs
          navigate(WORKBOARD_CURRENT_JOBS_URL);
        }
      },
      url: WORKBOARD_CURRENT_JOBS_URL,
      subMenu: workboardSubMenuItems,
    }] : []),
    ...(((userIsSuperuser || userIsStaff) && userHasCompanies && currentCompany) ? [{
      slug: 'reports',
      title: 'Reports',
      isParent: true,
      icon: <AssignmentIcon/>,
      handleClick: () => {
        navigate(dashboardCompanyOverviewUrl(currentCompany?.slug ?? ''));
      },
      url: dashboardCompanyOverviewUrl(currentCompany?.slug ?? ''),
      subMenu: reportsSubMenuItems,
    }] : []),
  ] as ITopMenuItems[], [currentCompany, navigate, userIsStaff, workboardSubMenuItems, reportsSubMenuItems, userIsSuperuser, userHasCompanies]);


  const settingsMenuItems = useMemo(
    () =>
      [
        {
          handleClick: () => {
            navigate(dashboardCompanyOverviewUrl(currentCompany?.slug ?? ''));
          },
          url: dashboardCompanySettingsUrl(currentCompany?.slug ?? ''),
          isParent: true,
          slug: SETTINGS_SLUG,
          title: 'Settings',
          subMenu: [
            { slug: SETTINGS_SLUG, title: 'Settings', url: dashboardCompanySettingsUrl(currentCompany?.slug ?? '') },
            {
              slug: SUBSCRIPTION_SLUG,
              title: 'Subscription',
              url: dashboardCompanySubscriptionUrl(currentCompany?.slug ?? ''),
            },
          ],
        },
      ] as ITopMenuItems[],
    [currentCompany, navigate]
  );

  const navigationItems = useMemo(() => {
    const currentNavItems = [] as INavigationItem[];

    if (currentSlug) {
      const navMenu = (COMPANY_DETAILS_SLUGS.includes(currentSlug) ? settingsMenuItems : topMenuItems).filter(
        menuItem => menuItem?.subMenu?.some(subItem => subItem.url.includes(`${currentSlug}`))
      )[0]?.subMenu;

      navMenu?.forEach(nav => {
        currentNavItems.push({
          id: nav.slug,
          title: nav.title,
          handleAction: () => navigate(nav.url),
        } as INavigationItem);
      });
    }

    return currentNavItems;
  }, [currentSlug, settingsMenuItems, topMenuItems, navigate]);

  const updatePageActions = useCallback(
    (newActions: PageActionsProps | null | undefined) => {
      if (!userIsStaff) {
        setPageActions(undefined);
      } else {
        setPageActions(newActions);
      }
    },
    [userIsStaff, setPageActions]
  );

  const companyDashboardProviderValue = useMemo(
    () => ({
      pageActions: pageActions ?? undefined,
      updatePageActions,
      editMode,
      setEditMode,
    }),
    [editMode, pageActions, updatePageActions]
  );
  
  return (
    <AttioValuationProvider>
      <CompanyDashboardContext.Provider value={companyDashboardProviderValue}>
        <Sidebar
          topMenuItems={topMenuItems}
          bottomMenuItems={bottomItems}
          logoUrl={currentCompany?.logo_url ?? ''}
          isLoading={showLoadingProgressBar}
        />

        <MainContainer
          currentCompany={currentCompany}
          isLoading={showLoadingProgressBar}
          navigationItems={navigationItems}
        >
          {children}
        </MainContainer>
      </CompanyDashboardContext.Provider>
    </AttioValuationProvider>
  );
};

export default DashboardLayout;
